import { AppConstants } from "@/app.constants";
import { idbStorage } from "./idbStorage";
import { DefectCodes, DefectSeverity } from "@/types/DefectCategory.type";
import { DefectDataByDivision } from "@/store/types/InspectionDefectData.type";

export type MasterDataEntityKey =
  | "md_controlPoints"
  | "md_divisions"
  | "md_clientDecisions"
  | "md_imageCategories"
  | "md_severities"
  | `md_defects:${string}`;

const STORE_NAME = AppConstants.INDEX_DB.store.masterData;

const serialize = <T>(value: T): string => JSON.stringify(value);
const deserialize = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const masterDataIdbService = {
  getEntity: async <T>(key: MasterDataEntityKey): Promise<T | null> => {
    const raw = await idbStorage.getItem(STORE_NAME, key);
    return deserialize<T>(raw);
  },

  setEntity: async <T>(key: MasterDataEntityKey, value: T): Promise<void> => {
    const serialized = serialize(value);
    await idbStorage.setItem(STORE_NAME, key, serialized);
  },

  removeEntity: async (key: MasterDataEntityKey): Promise<void> => {
    await idbStorage.removeItem(STORE_NAME, key);
  },

  clearAll: async (): Promise<void> => {
    await idbStorage.clear(STORE_NAME);
  },

  /**
   * Defect-specific helpers
   */
  getDefectsByDivision: async (
    controlPointId: string,
    divisionId: string
  ): Promise<DefectCodes[] | null> => {
    const key: MasterDataEntityKey = `md_defects:${controlPointId}:${divisionId}`;
    const data = await masterDataIdbService.getEntity<{
      controlPointId: string;
      divisionId: string;
      defects: DefectCodes[];
    }>(key);
    return data?.defects ?? null;
  },

  setDefectsByDivision: async (
    controlPointId: string,
    divisionId: string,
    defects: DefectCodes[]
  ): Promise<void> => {
    const key: MasterDataEntityKey = `md_defects:${controlPointId}:${divisionId}`;
    await masterDataIdbService.setEntity(key, { controlPointId, divisionId, defects });
  },

  getAllDefectDivisions: async (
    controlPointId: string
  ): Promise<DefectDataByDivision[]> => {
    const keys = await idbStorage.getAllKeys(STORE_NAME);
    const result: DefectDataByDivision[] = [];

    const prefix = `md_defects:${controlPointId}:`;
    const defectKeys = (keys as string[]).filter((k) => k.startsWith(prefix));

    for (const key of defectKeys) {
      const raw = await idbStorage.getItem(STORE_NAME, key);
      const parsed = deserialize<{
        controlPointId?: string;
        divisionId: string;
        defects: DefectCodes[];
      }>(raw);
      if (parsed && parsed.divisionId !== undefined) {
        result.push({
          divisionId: parsed.divisionId,
          defects: parsed.defects || [],
        });
      }
    }

    return result;
  },

  getSeverities: async (): Promise<{ controlPointId: string; severities: DefectSeverity[] } | null> => {
    return masterDataIdbService.getEntity<{ controlPointId: string; severities: DefectSeverity[] }>("md_severities");
  },

  setSeverities: async (controlPointId: string, severities: DefectSeverity[]): Promise<void> => {
    await masterDataIdbService.setEntity("md_severities", { controlPointId, severities });
  },
};

